import { useCallback, useMemo } from 'react'
import { makeCrossSectionLabel } from '@editorUtils'
import { keyBy, uniq } from 'lodash-es'
import { useModelStore } from '@editorStores'
import {
  useElementCrossSectionAssignment,
  useElementLabel,
  useWallIdToAssembly,
} from '@editorHooks'
import { ElementListItem, ElementListTab } from '../types'
import { buildElementListItems } from '../utils/buildElementListItems'

const WALL_USAGE_PREFIX: Partial<Record<ElementTypes, string>> = {
  inner_walls: 'IW',
  outer_walls: 'AW',
}

/**
 * Shared hook that builds the element list items, keyed by element type, from the global model +
 * system-manager stores. Reused by every page that shows an element list pane — pages only differ
 * in how they wire selection, not in how items are produced.
 */
const useElementListItems = (tabs: ElementListTab[]): Record<string, ElementListItem[]> => {
  const model = useModelStore(state => state.model)
  const getElementLabel = useElementLabel()
  const crossSectionAssignment = useElementCrossSectionAssignment()
  const elementIdToAssembly = useWallIdToAssembly()

  const elementTypes = useMemo(
    () => uniq(tabs.flatMap(tab => tab.subTabs.flatMap(subTab => subTab.elementTypes))),
    [tabs],
  )

  const assignmentByGuid = useMemo(
    () => keyBy(crossSectionAssignment, 'element_guid'),
    [crossSectionAssignment],
  )

  const getCrossSectionLabel = useCallback(
    (id: string): string | undefined => {
      const crossSection = assignmentByGuid[id]?.element_cs
      return crossSection ? makeCrossSectionLabel(crossSection) : undefined
    },
    [assignmentByGuid],
  )

  const getAssemblyName = useCallback(
    (id: string, elementType: ElementTypes): string | undefined => {
      const assembly = elementIdToAssembly[id]
      if (!assembly) return undefined
      return [WALL_USAGE_PREFIX[elementType], assembly.name?.trim()].filter(Boolean).join(' · ')
    },
    [elementIdToAssembly],
  )

  return useMemo(
    () =>
      buildElementListItems(model, elementTypes, {
        getElementLabel,
        getCrossSectionLabel,
        getAssemblyName,
      }),
    [model, elementTypes, getElementLabel, getCrossSectionLabel, getAssemblyName],
  )
}

export default useElementListItems
